import { useCallback } from 'react';
import { useTranslation } from '~/_shared/utils/hooks';
import { useGetMarkersInArea } from '~/map/map/lassoTool/useGetMarkersInArea';
import { getTargetNumberOfTerritories } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.helpers';
import { BuildingBlockSelection } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/createBoundaryGroupFromAIModal.types';
import { BoundaryMatchingType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import type { CreateOptimizedTerritoriesConfig } from './createOptimizedTerritories.types';

// Each requested territory must be backed by at least this many data points.
export const OPTIMIZED_TERRITORY_MIN_LOCATIONS_PER_TERRITORY = 10;

export const useOptimizedTerritoriesLocationCountValidation = () => {
  const [t] = useTranslation();
  const { getMarkersInArea } = useGetMarkersInArea();

  const validate = useCallback(async (config: CreateOptimizedTerritoriesConfig, markerFilterActive: boolean): Promise<string | null> => {
    const usesSpreadsheetData = config.dataMetricsValues.some(metric => metric.checked);
    const targetNumberOfTerritories = getTargetNumberOfTerritories(config);

    if (!usesSpreadsheetData || !targetNumberOfTerritories) {
      return null;
    }

    const usesBoundarySubset = config.buildingBlockSelection === BuildingBlockSelection.SUBSET
      && config.selectedBoundaryIds.length > 0
      && !!config.boundaryGroupId;

    // Combine the map filter (if applied to data) and the boundary subset (if a subset of building blocks is used)
    // into a single request, so the count reflects both constraints at once rather than each in isolation.
    const rowsPerSpreadsheet = await getMarkersInArea({
      useMainFilters: markerFilterActive && config.applyFilterOnData,
      boundaries: (usesBoundarySubset && config.boundaryGroupId) ? {
        [config.boundaryGroupId]: {
          boundary_ids: config.selectedBoundaryIds,
          matchings: { matching_type: BoundaryMatchingType.Geometry },
        },
      } : undefined,
    });

    const relevantLocationCount = rowsPerSpreadsheet.reduce((sum, item) => sum + item.rows.length, 0);
    const minimumRequiredLocations = targetNumberOfTerritories * OPTIMIZED_TERRITORY_MIN_LOCATIONS_PER_TERRITORY;

    if (relevantLocationCount < minimumRequiredLocations) {
      return t('boundaryAI.notEnoughDataPointsForTerritories', {
        required: minimumRequiredLocations,
        territories: targetNumberOfTerritories,
        available: relevantLocationCount,
      });
    }

    return null;
  }, [getMarkersInArea, t]);

  return {
    validate,
  };
};
